/**
 * GIF frame delays exactly as Phira reads them (W1-13 persist-workers; contracted export `gifDelaysMs`).
 *
 * Phira takes each frame's delay from the Graphics Control Extension (GCE) that precedes the frame's image
 * descriptor, in centiseconds, multiplied by 10, with NO clamping (browsers turn 0/1 cs into 100 ms; Phira does
 * not; rpe-format.md F§9.7: a GIF whose delays sum to 0 hangs Phira, the checker reports it). A frame without a
 * GCE has delay 0. A GCE applies to the next image only.
 *
 * The parser walks the block structure of the GIF87a/GIF89a container (header, logical screen descriptor,
 * optional global colour table, then extension / image / trailer blocks with their sub-block chains) without
 * decoding pixels. A truncated file yields the delays of the frames whose image data was complete.
 */

const EXTENSION = 0x21;
const IMAGE = 0x2c;
const TRAILER = 0x3b;
const GCE_LABEL = 0xf9;

function isGifHeader(bytes: Uint8Array): boolean {
  if (bytes.length < 13) return false;
  // "GIF87a" or "GIF89a"
  return (
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  );
}

/** Position after a sub-block chain starting at `pos` (size byte + data, ..., 0), or -1 when truncated. */
function skipSubBlocks(bytes: Uint8Array, pos: number): number {
  let p = pos;
  for (;;) {
    const size = bytes[p];
    if (size === undefined) return -1;
    p += 1;
    if (size === 0) return p;
    p += size;
    if (p > bytes.length) return -1;
  }
}

/** Size in bytes of a colour table announced by a packed field (bit 7 = present, bits 0-2 = size). */
function colorTableBytes(packed: number): number {
  return (packed & 0x80) === 0 ? 0 : 3 * (1 << ((packed & 0x07) + 1));
}

/**
 * Per-frame delays in milliseconds (GCE delay x 10, unclamped) of a GIF file, in frame order; `null` when the
 * bytes are not a GIF. An empty list means a GIF without any complete frame.
 */
export const gifDelaysMs: (bytes: Uint8Array) => readonly number[] | null = (bytes) => {
  if (!isGifHeader(bytes)) return null;
  const delays: number[] = [];
  let pos = 13 + colorTableBytes(bytes[10] ?? 0);
  let pendingCs = 0;
  while (pos < bytes.length) {
    const block = bytes[pos];
    pos += 1;
    if (block === TRAILER) break;
    if (block === EXTENSION) {
      const label = bytes[pos];
      if (label === undefined) break;
      pos += 1;
      if (label === GCE_LABEL) {
        const size = bytes[pos] ?? 0;
        if (size >= 4 && pos + 3 < bytes.length) {
          pendingCs = (bytes[pos + 2] ?? 0) | ((bytes[pos + 3] ?? 0) << 8);
        }
      }
      const next = skipSubBlocks(bytes, pos);
      if (next < 0) break;
      pos = next;
      continue;
    }
    if (block === IMAGE) {
      // Image descriptor: left(2) top(2) width(2) height(2) packed(1).
      if (pos + 9 > bytes.length) break;
      const packed = bytes[pos + 8] ?? 0;
      pos += 9 + colorTableBytes(packed);
      // LZW minimum code size, then the image data sub-blocks.
      pos += 1;
      if (pos > bytes.length) break;
      const next = skipSubBlocks(bytes, pos);
      if (next < 0) break;
      pos = next;
      delays.push(pendingCs * 10);
      pendingCs = 0;
      continue;
    }
    // Unknown block type: the stream is corrupt from here on.
    break;
  }
  return delays;
};

/** Sum of the frame delays (0 = Phira hangs on this GIF). */
export function gifTotalMs(delays: readonly number[]): number {
  let total = 0;
  for (const d of delays) total += d;
  return total;
}
